import { osnovaApi } from '../base';
import type { ArchiveAnswerResult, ArchiveSearchResult, Meeting, MeetingAssignment, MeetingDepartment, MeetingDepartmentCount, MeetingEmployee, MeetingParticipant, MeetingProtocol, MeetingProtocolItem, MeetingSeries, MeetingSeriesSummary, MeetingTranscript, ParticipantImportResult, MeetingTranscriptSegment, MeetingsInfo, MyAssignment, RegisterAssignment, RegisterDecision, UserCandidate } from '@entities/meetings/types';

export const meetingsApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    getMeetingsInfo: b.query<MeetingsInfo, void>({
      query: () => '/agents/meetings/info',
      providesTags: ['Meetings'],
    }),
    /** Последние совещания (витрина: статус обработки, заголовок, дата).
     *  `deleted: true` — показать и помеченные на удаление (корзина, только админ). */
    getMeetings: b.query<{ meetings: Meeting[] }, { deleted?: boolean } | void>({
      query: (arg) => ({
        url: '/agents/meetings/meetings',
        params: arg && arg.deleted ? { deleted: true } : undefined,
      }),
      providesTags: ['Meetings'],
    }),
    /** Совещания одного отдела (страница/папка отдела, блок E). */
    getMeetingsByDepartment: b.query<{ meetings: Meeting[] }, string>({
      query: (departmentId) => ({
        url: '/agents/meetings/meetings',
        params: { department_id: departmentId },
      }),
      providesTags: ['Meetings'],
    }),
    /** Сводка совещаний по отделам (папки витрины + «без отдела», блок E). */
    getMeetingsDepartmentsOverview: b.query<{ departments: MeetingDepartmentCount[] }, void>({
      query: () => '/agents/meetings/departments-overview',
      providesTags: ['Meetings'],
    }),
    /** Накопительный реестр (N): сквозной свод действующих решений + поручений с
     *  фильтрами отдел/серия/исполнитель/статус. Детерминированно, без LLM. */
    getMeetingsRegister: b.query<
      { decisions: RegisterDecision[]; assignments: RegisterAssignment[]; generated_at: string },
      { department_id?: string; series_id?: string; assignee_id?: string; status?: string } | void
    >({
      query: (f) => {
        const params: Record<string, string> = {};
        if (f) {
          if (f.department_id) params.department_id = f.department_id;
          if (f.series_id) params.series_id = f.series_id;
          if (f.assignee_id) params.assignee_id = f.assignee_id;
          if (f.status) params.status = f.status;
        }
        return { url: '/agents/meetings/register', params };
      },
      providesTags: ['Meetings', 'MeetingAssignments'],
    }),
    /** Скачать DOCX накопительного реестра как Blob (те же фильтры). Лениво — по клику. */
    exportMeetingsRegisterDocx: b.query<
      Blob,
      { department_id?: string; series_id?: string; assignee_id?: string; status?: string } | void
    >({
      query: (f) => {
        const params: Record<string, string> = {};
        if (f) {
          if (f.department_id) params.department_id = f.department_id;
          if (f.series_id) params.series_id = f.series_id;
          if (f.assignee_id) params.assignee_id = f.assignee_id;
          if (f.status) params.status = f.status;
        }
        return {
          url: '/agents/meetings/register/docx',
          params,
          cache: 'no-store',
          responseHandler: (response) => response.blob(),
        };
      },
    }),
    /** Загрузить запись (multipart `file` + опц. `title`/`series_id`/`project_id`): создаёт
     *  совещание + запись, ставит ASR-задачу; ответ — созданное совещание. */
    uploadMeetingRecording: b.mutation<{ meeting: Meeting }, FormData>({
      query: (body) => ({ url: '/agents/meetings/recordings', method: 'POST', body }),
      invalidatesTags: ['Meetings'],
    }),
    /** Загрузить готовый протокол (DOCX/TXT/PDF) — совещание без записи, разбор LLM (U). */
    uploadMeetingProtocolDoc: b.mutation<{ meeting: Meeting }, FormData>({
      query: (body) => ({ url: '/agents/meetings/protocol-upload', method: 'POST', body }),
      invalidatesTags: ['Meetings'],
    }),
    /** Одно совещание (детальная страница). */
    getMeeting: b.query<{ meeting: Meeting }, string>({
      query: (id) => `/agents/meetings/meetings/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Meetings', id }],
    }),
    /** Пометить совещание на удаление (soft-delete, 30 дней на восстановление). Только админ. */
    deleteMeeting: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/meetings/meetings/${id}/delete`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Meetings', id }, 'Meetings'],
    }),
    /** Восстановить помеченное на удаление совещание (в течение 30 дней). Только админ. */
    restoreMeeting: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/meetings/meetings/${id}/restore`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Meetings', id }, 'Meetings'],
    }),
    /** Проставить/сбросить отдел совещания вручную (HITL, блок E). */
    setMeetingDepartment: b.mutation<
      { ok: boolean; department_id: string | null; department_name: string | null },
      { meetingId: string; department_id: string | null }
    >({
      query: ({ meetingId, department_id }) => ({
        url: `/agents/meetings/meetings/${meetingId}/department`,
        method: 'POST',
        body: { department_id },
      }),
      invalidatesTags: (_r, _e, { meetingId }) => [{ type: 'Meetings', id: meetingId }, 'Meetings'],
    }),
    /** Сквозная привязка совещания к серии, строительному объекту ПТО и/или договору. */
    setMeetingContext: b.mutation<
      {
        ok: boolean;
        series_id: string | null;
        department_id?: string | null;
        project_id: string | null;
        contract_id: string | null;
      },
      {
        meetingId: string;
        series_id?: string | null;
        department_id?: string | null;
        project_id?: string | null;
        contract_id?: string | null;
      }
    >({
      query: ({ meetingId, series_id, department_id, project_id, contract_id }) => ({
        url: `/agents/meetings/meetings/${meetingId}/context`,
        method: 'POST',
        body: {
          series_id: series_id ?? null,
          department_id: department_id ?? null,
          project_id: project_id ?? null,
          contract_id: contract_id ?? null,
        },
      }),
      invalidatesTags: (_r, _e, { meetingId }) => [
        { type: 'Meetings', id: meetingId },
        { type: 'MeetingAssignments', id: meetingId },
        'Meetings',
      ],
    }),
    /** Протокол совещания + пункты (повестка/решения/поручения/разногласия). */
    getMeetingProtocol: b.query<
      { protocol: MeetingProtocol; items: MeetingProtocolItem[] },
      string
    >({
      query: (id) => `/agents/meetings/meetings/${id}/protocol`,
      providesTags: (_r, _e, id) => [{ type: 'MeetingProtocol', id }],
    }),
    /** Утвердить черновик протокола (организатор) — материализует поручения. */
    approveMeetingProtocol: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/meetings/meetings/${id}/protocol/approve`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'MeetingProtocol', id },
        { type: 'Meetings', id },
        { type: 'MeetingAssignments', id },
        'MyMeetingAssignments',
        'Meetings',
      ],
    }),
    /** Отклонить черновик протокола (на доработку). */
    declineMeetingProtocol: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/meetings/meetings/${id}/protocol/decline`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'MeetingProtocol', id },
        { type: 'Meetings', id },
        'Meetings',
      ],
    }),
    /** Сменить статус пункта протокола (контроль решений/поручений 2.6): снять с контроля
     *  (superseded/cancelled) или вернуть (active). `meetingId` — для инвалидации
     *  протокола и поручений совещания; реестр действующих решений обновляется тегом Meetings. */
    setMeetingProtocolItemStatus: b.mutation<
      { ok: boolean },
      { itemId: string; status: string; superseded_by_item_id?: string; meetingId: string }
    >({
      query: ({ itemId, status, superseded_by_item_id }) => ({
        url: `/agents/meetings/protocol-items/${itemId}/status`,
        method: 'POST',
        body: { status, superseded_by_item_id },
      }),
      invalidatesTags: (_r, _e, { meetingId }) => [
        { type: 'MeetingProtocol', id: meetingId },
        { type: 'MeetingAssignments', id: meetingId },
        'MyMeetingAssignments',
        'Meetings',
      ],
    }),
    /** Ручная правка пункта протокола (HITL) до утверждения: текст/ответственный/срок.
     *  `meetingId` — для инвалидации протокола. Гейт draft-only на сервере. */
    editMeetingProtocolItem: b.mutation<
      { ok: boolean; item: MeetingProtocolItem },
      {
        itemId: string;
        text: string;
        responsible_name?: string | null;
        due_text?: string | null;
        meetingId: string;
      }
    >({
      query: ({ itemId, text, responsible_name, due_text }) => ({
        url: `/agents/meetings/protocol-items/${itemId}/edit`,
        method: 'POST',
        body: { text, responsible_name, due_text },
      }),
      invalidatesTags: (_r, _e, { meetingId }) => [
        { type: 'MeetingProtocol', id: meetingId },
        'Meetings',
      ],
    }),
    /** Реестр поручений совещания. */
    getMeetingAssignments: b.query<{ assignments: MeetingAssignment[] }, string>({
      query: (id) => `/agents/meetings/meetings/${id}/assignments`,
      providesTags: (_r, _e, id) => [{ type: 'MeetingAssignments', id }],
    }),
    /** Повторно сопоставить исполнителей поручений со справочником сотрудников
     *  (после пополнения справочника). Ново-сопоставленным уходит адресная
     *  рассылка с кнопкой «Выполнено». Аргумент — id совещания. */
    rematchMeetingAssignments: b.mutation<{ matched: number; notified: number }, string>({
      query: (id) => ({
        url: `/agents/meetings/meetings/${id}/assignments/rematch`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'MeetingAssignments', id },
        'MeetingAssignments',
        'MyMeetingAssignments',
      ],
    }),
    /** Сменить статус поручения. `meetingId` — для инвалидации реестра совещания. */
    setMeetingAssignmentStatus: b.mutation<
      { ok: boolean },
      { id: string; status: string; meetingId: string }
    >({
      query: ({ id, status }) => ({
        url: `/agents/meetings/assignments/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (_r, _e, { meetingId }) => [
        { type: 'MeetingAssignments', id: meetingId },
        'MeetingAssignments',
        'MyMeetingAssignments',
      ],
    }),
    /** Установить/перенести срок поручения (право записи). `due_date` = 'ГГГГ-ММ-ДД'
     *  или null (снять срок). Сбрасывает эскалацию на сервере. */
    setMeetingAssignmentDue: b.mutation<
      { ok: boolean },
      { id: string; due_date: string | null; meetingId: string }
    >({
      query: ({ id, due_date }) => ({
        url: `/agents/meetings/assignments/${id}/due`,
        method: 'POST',
        body: { due_date },
      }),
      invalidatesTags: (_r, _e, { meetingId }) => [
        { type: 'MeetingAssignments', id: meetingId },
        'MeetingAssignments',
        'MyMeetingAssignments',
      ],
    }),
    /** Передать поручение совещания в целевой модуль («Единый организм»). */
    dispatchMeetingAssignment: b.mutation<
      {
        ok: boolean;
        target_agent: string;
        linked_entity_type: string | null;
        linked_entity_id: string | null;
      },
      { id: string; target_agent?: string | null; meetingId?: string }
    >({
      query: ({ id, target_agent }) => ({
        url: `/agents/meetings/assignments/${id}/dispatch`,
        method: 'POST',
        body: { target_agent: target_agent ?? null },
      }),
      invalidatesTags: (_r, _e, { meetingId }) => [
        ...(meetingId ? [{ type: 'MeetingAssignments' as const, id: meetingId }] : []),
        'MeetingAssignments',
        'Meetings',
        'MyMeetingAssignments',
      ],
    }),
    /** Список серий совещаний. */
    listMeetingSeries: b.query<{ series: MeetingSeries[] }, { project_id?: string } | void>({
      query: (arg) => ({
        url: '/agents/meetings/series',
        params: arg && arg.project_id ? { project_id: arg.project_id } : undefined,
      }),
      providesTags: ['Meetings'],
    }),
    /** Создать серию совещаний (планёрку по объекту / штаб / ВКС отдела). */
    createMeetingSeries: b.mutation<
      { series: MeetingSeries },
      {
        title: string;
        department_id?: string | null;
        organizer_id?: string | null;
        project_id?: string | null;
        contract_id?: string | null;
        note?: string | null;
      }
    >({
      query: (body) => ({ url: '/agents/meetings/series', method: 'POST', body }),
      invalidatesTags: ['Meetings'],
    }),
    /** Скачать DOCX протокола совещания как Blob (RBAC через Authorization из
     *  baseQuery; `no-store` — документ мог быть пересобран). Лениво — по клику. */
    exportMeetingProtocolDocx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/meetings/meetings/${id}/protocol/docx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** Скачать DOCX контрольного листа серии как Blob (по `series_id`). Лениво. */
    exportMeetingSeriesSummaryDocx: b.query<Blob, string>({
      query: (seriesId) => ({
        url: `/agents/meetings/series/${seriesId}/summary/docx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** Живая сводка серии (действующие решения + открытые поручения) для онлайн-
     *  просмотра. Аргумент — series_id. */
    getMeetingSeriesSummary: b.query<MeetingSeriesSummary, string>({
      query: (seriesId) => `/agents/meetings/series/${seriesId}/summary`,
      providesTags: ['Meetings', 'MeetingAssignments'],
    }),
    /** Транскрипт совещания со спикерами/таймкодами (после ASR-обработки записи). */
    getMeetingTranscript: b.query<
      { transcript: MeetingTranscript; segments: MeetingTranscriptSegment[] },
      string
    >({
      query: (id) => `/agents/meetings/meetings/${id}/transcript`,
      providesTags: (_r, _e, id) => [{ type: 'Meetings', id }],
    }),
    /** «Мои поручения» — назначенные на текущего пользователя по всем совещаниям. */
    getMyAssignments: b.query<{ assignments: MyAssignment[] }, void>({
      query: () => '/agents/meetings/my-assignments',
      providesTags: ['MyMeetingAssignments'],
    }),
    /** Исполнитель меняет статус СВОЕГО поручения (доступ по владению, без can_write).
     *  `meetingId` — для инвалидации реестра совещания заодно с личным списком. */
    setMyAssignmentStatus: b.mutation<
      { ok: boolean; status: string },
      { id: string; status: string; meetingId: string }
    >({
      query: ({ id, status }) => ({
        url: `/agents/meetings/my-assignments/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (_r, _e, { meetingId }) => [
        'MyMeetingAssignments',
        'MeetingAssignments',
        { type: 'MeetingAssignments', id: meetingId },
      ],
    }),
    /** Участники совещания (для адресной рассылки протокола). */
    getMeetingParticipants: b.query<{ participants: MeetingParticipant[] }, string>({
      query: (id) => `/agents/meetings/meetings/${id}/participants`,
      providesTags: (_r, _e, id) => [{ type: 'MeetingParticipants', id }],
    }),
    /** Справочник активных учёток для привязки участника к пользователю. */
    getMeetingUserCandidates: b.query<{ candidates: UserCandidate[] }, void>({
      query: () => '/agents/meetings/user-candidates',
      providesTags: ['Meetings'],
    }),
    /** Добавить участника совещания (имя + опц. VK-адрес для рассылки + опц. учётка). */
    addMeetingParticipant: b.mutation<
      { participant: MeetingParticipant },
      { meetingId: string; name: string; vk_chat_id?: string; user_id?: string }
    >({
      query: ({ meetingId, name, vk_chat_id, user_id }) => ({
        url: `/agents/meetings/meetings/${meetingId}/participants`,
        method: 'POST',
        body: { name, vk_chat_id, user_id },
      }),
      invalidatesTags: (_r, _e, { meetingId }) => [{ type: 'MeetingParticipants', id: meetingId }],
    }),
    /** Добавить участников списком (выгрузка приглашённых из календаря VK WorkSpace /
     *  вставленный список): сервер сопоставляет со справочником по email и ФИО,
     *  уже добавленных не дублирует. */
    importMeetingParticipants: b.mutation<
      ParticipantImportResult,
      { meetingId: string; rows: { name: string; email?: string }[] }
    >({
      query: ({ meetingId, rows }) => ({
        url: `/agents/meetings/meetings/${meetingId}/participants/import`,
        method: 'POST',
        body: { rows },
      }),
      invalidatesTags: (_r, _e, { meetingId }) => [{ type: 'MeetingParticipants', id: meetingId }],
    }),
    /** Пересобрать участников из протокола: имена L-2 → справочник сотрудников,
     *  плюс фолбэк по перекличке (для протоколов, где L-2 отнёс присутствовавших
     *  к «упомянутым»). Идемпотентно: добавленных ранее не дублирует и не удаляет. */
    rematchMeetingParticipants: b.mutation<{ added: number }, string>({
      query: (id) => ({
        url: `/agents/meetings/meetings/${id}/participants/rematch`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'MeetingParticipants', id }],
    }),
    /** Удалить участника совещания (soft-delete). `meetingId` — для инвалидации списка. */
    deleteMeetingParticipant: b.mutation<{ ok: boolean }, { id: string; meetingId: string }>({
      query: ({ id }) => ({
        url: `/agents/meetings/participants/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { meetingId }) => [{ type: 'MeetingParticipants', id: meetingId }],
    }),
    /** Справочник отделов (классификация решений/поручений по отделам, D). */
    listMeetingDepartments: b.query<{ departments: MeetingDepartment[] }, void>({
      query: () => `/agents/meetings/departments`,
      providesTags: ['MeetingDepartments'],
    }),
    /** Добавить отдел в справочник. */
    createMeetingDepartment: b.mutation<{ department: MeetingDepartment }, { name: string }>({
      query: (body) => ({ url: `/agents/meetings/departments`, method: 'POST', body }),
      invalidatesTags: ['MeetingDepartments'],
    }),
    /** Удалить отдел из справочника (soft-delete). */
    deleteMeetingDepartment: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/meetings/departments/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MeetingDepartments'],
    }),
    /** Справочник сотрудников с VK chat_id (0157): авто-сопоставление участников. */
    listMeetingEmployees: b.query<{ employees: MeetingEmployee[] }, void>({
      query: () => `/agents/meetings/employees`,
      providesTags: ['MeetingEmployees'],
    }),
    /** Массовая загрузка справочника сотрудников (bulk-upsert по ФИО, идемпотентно). */
    importMeetingEmployees: b.mutation<
      { ok: boolean; upserted: number },
      {
        employees: {
          full_name: string;
          vk_chat_id?: string | null;
          department?: string | null;
          position?: string | null;
        }[];
      }
    >({
      query: (body) => ({ url: `/agents/meetings/employees`, method: 'POST', body }),
      invalidatesTags: ['MeetingEmployees', 'MeetingDepartments'],
    }),
    /** Синхронизировать справочник сотрудников и отделов с пользователями системы (`users`). */
    syncMeetingEmployees: b.mutation<{ ok: boolean; synced: number }, void>({
      query: () => ({ url: `/agents/meetings/employees/sync-users`, method: 'POST' }),
      invalidatesTags: ['MeetingEmployees', 'MeetingDepartments'],
    }),
    /** Удалить сотрудника из справочника (soft-delete). */
    deleteMeetingEmployee: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/meetings/employees/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MeetingEmployees'],
    }),
    /** Поиск по архиву утверждённых протоколов (этап 3.1): гибрид лексика+вектор,
     *  возвращает цитаты (совещание+пункт+таймкод). POST-тело; дёргается лениво по
     *  кнопке (не читается на маунте). */
    searchMeetingsArchive: b.query<
      ArchiveSearchResult,
      { query: string; top_k?: number; department_id?: string; series_id?: string }
    >({
      query: (body) => ({ url: '/agents/meetings/archive/search', method: 'POST', body }),
    }),
    /** RAG-ответ по архиву (этап 3.1): LLM синтезирует ответ СТРОГО со ссылками на
     *  найденные пункты (§3.1 — без цитаты нет ответа). Генеративное действие →
     *  mutation. */
    answerMeetingsArchive: b.mutation<
      ArchiveAnswerResult,
      { query: string; top_k?: number; department_id?: string; series_id?: string }
    >({
      query: (body) => ({ url: '/agents/meetings/archive/answer', method: 'POST', body }),
    }),
    /** Ручная переиндексация архива (админ): возвращает число поставленных job'ов. */
    reindexMeetingsArchive: b.mutation<
      { ok: boolean; enqueued: number; rag_enabled: boolean },
      { force?: boolean } | void
    >({
      query: (body) => ({
        url: '/agents/meetings/archive/reindex',
        method: 'POST',
        body: body ?? {},
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMeetingsInfoQuery,
  useGetMeetingsQuery,
  useGetMeetingsByDepartmentQuery,
  useGetMeetingsDepartmentsOverviewQuery,
  useGetMeetingsRegisterQuery,
  useLazyExportMeetingsRegisterDocxQuery,
  useUploadMeetingRecordingMutation,
  useUploadMeetingProtocolDocMutation,
  useGetMeetingQuery,
  useDeleteMeetingMutation,
  useRestoreMeetingMutation,
  useSetMeetingDepartmentMutation,
  useSetMeetingContextMutation,
  useGetMeetingProtocolQuery,
  useApproveMeetingProtocolMutation,
  useDeclineMeetingProtocolMutation,
  useSetMeetingProtocolItemStatusMutation,
  useEditMeetingProtocolItemMutation,
  useGetMeetingAssignmentsQuery,
  useRematchMeetingAssignmentsMutation,
  useSetMeetingAssignmentStatusMutation,
  useDispatchMeetingAssignmentMutation,
  useListMeetingSeriesQuery,
  useCreateMeetingSeriesMutation,
  useLazyExportMeetingProtocolDocxQuery,
  useLazyExportMeetingSeriesSummaryDocxQuery,
  useGetMeetingSeriesSummaryQuery,
  useGetMeetingTranscriptQuery,
  useGetMyAssignmentsQuery,
  useSetMyAssignmentStatusMutation,
  useSetMeetingAssignmentDueMutation,
  useGetMeetingUserCandidatesQuery,
  useGetMeetingParticipantsQuery,
  useAddMeetingParticipantMutation,
  useRematchMeetingParticipantsMutation,
  useImportMeetingParticipantsMutation,
  useDeleteMeetingParticipantMutation,
  useListMeetingDepartmentsQuery,
  useCreateMeetingDepartmentMutation,
  useDeleteMeetingDepartmentMutation,
  useListMeetingEmployeesQuery,
  useImportMeetingEmployeesMutation,
  useSyncMeetingEmployeesMutation,
  useDeleteMeetingEmployeeMutation,
  useLazySearchMeetingsArchiveQuery,
  useAnswerMeetingsArchiveMutation,
  useReindexMeetingsArchiveMutation,
} = meetingsApi;
